





































import { Component, Prop, Mixins } from "vue-property-decorator";
import { ITaxTagLocation } from "@/models/tax";
import HasValidation from "@/mixins/HasValidation";
import CountriesStore from "@/mixins/constants/countriesStore";
import { mapState } from "vuex";
import { IState } from "@/store";
import { ICountry, IRegion } from "@/models/constants";
import { CreateElement } from "vue";

@Component({
  components: {
    "region-field": () =>
      import("@/components/app/global/address/regionField.vue"),
    "country-select": () => import("@/components/base/CountrySelect.vue")
  },
  computed: {
    ...mapState({
      brandCountryId: (state: IState) => state.brand.country_id,
      regions(state: IState, getters) {
        return getters[`constants/getRegions`](
          this.$_.get(this.country, "code")
        );
      }
    })
  }
})
export default class TaxLocationSelect extends Mixins(
  HasValidation,
  CountriesStore
) {
  // Props

  @Prop({ default: null })
  public taxLocation!: ITaxTagLocation;

  @Prop({ default: "plus" })
  public ctaIcon!: string;

  @Prop({ default: undefined })
  public ctaType!: string;

  @Prop({ default: false })
  public focus!: boolean;

  // Data

  public selectedCountryIds = [];
  public regions!: IRegion[];
  public brandCountryId!: ICountry["id"];
  public form: Partial<ITaxTagLocation> = this.initForm();
  public postCode = "";
  public isProcessing = false;
  public isLoadingRegions = false;

  // Life-cycle methods

  public async created() {
    if (this.taxLocation) {
      this.form = { ...this.taxLocation };
    } else {
      this.setDefaultCountry();
    }
    await this.loadRegions();
  }

  // Computed

  public get isCountryRegionless() {
    return !this.regions?.length;
  }

  public get isValid() {
    if (this.hasErrors) return false;
    if (!this.form.country_id) return false;
    return true;
  }

  public get isCountrySelected() {
    return this.form.country_id;
  }

  public get isFormChanged() {
    return !this.$_.isEqual(this.form, this.initForm());
  }

  public get country() {
    if (!this.isCountrySelected) return null;
    return this.$_.find(
      this.countries,
      country => country.id === this.form.country_id
    );
  }

  public get region() {
    if (!this.isCountrySelected) return null;
    return this.$_.find(
      this.regions,
      region => region.id === this.form.region_id
    );
  }

  public get countrySelectRenderer() {
    return {
      functional: true,
      render: (h: CreateElement) => {
        return h("country-select", {
          props: {
            value: this.form.country_id,
            disabled: this.isProcessing
          },
          attrs: { placeholder: this.$t("_.select_country") },
          on: {
            input: async val => {
              this.form.country_id = val;
              this.loadRegions();
            }
          }
        });
      }
    };
  }

  public get regionRenderer() {
    if (this.isCountryRegionless) return null;

    return {
      functional: true,
      render: (h: CreateElement) => {
        return h("region-field", {
          props: {
            country: this.country,
            isDisabled: this.isLoadingRegions,
            value: this.form.region_id
          },
          on: {
            input: val => {
              this.form.region_id = val;
            }
          }
        });
      }
    };
  }

  public loadRegions() {
    if (!this.isCountrySelected) return;
    this.isLoadingRegions = true;
    return this.$store
      .dispatch("constants/getRegions", {
        countryId: this.$_.get(this.country, "id"),
        countryCode: this.$_.get(this.country, "code")
      })
      .finally(() => (this.isLoadingRegions = false));
  }

  public initForm(form?: Partial<ITaxTagLocation>): Partial<ITaxTagLocation> {
    return {
      country_id: undefined,
      region_id: undefined,
      post_code: undefined,
      ...form
    };
  }

  public setDefaultCountry() {
    if (!this.form.country_id) {
      this.form.country_id = this.brandCountryId;
    }
  }

  public buildFormWithRelations() {
    return {
      ...this.form,
      country: this.countries?.find(i => i.id === this.form.country_id),
      region: this.regions?.find(i => i.id === this.form.region_id)
    };
  }

  public async validateBeforeSubmit() {
    try {
      this.isProcessing = true;
      const isValid = await this.validate();
      if (isValid) {
        this.$emit("success", this.buildFormWithRelations());
        this.form = this.initForm({ country_id: this.form.country_id });
      } else {
        throw this.$t("_sentence.form_is_not_valid");
      }
    } catch (error) {
      this.$store.dispatch("api/handleValidationError", {
        error,
        vm: this
      });
    } finally {
      this.isProcessing = false;
    }
  }
}
